/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { Rect } from "./getRelativeRect";
import { ClientRectObject } from "./getBoundingClientRect";
import { PopperPlacementType } from "../components/core";
import { getBasePopperPlacement } from "./getBasePopperPlacement";
import { getPopperPlacementVariation } from "./getPopperPlacementVariation";

export function computeRelativeOffset({
	elementRect,
	anchorRect,
	placement
}: {
	elementRect: Rect | ClientRectObject;
	anchorRect: Rect | ClientRectObject;
	placement?: PopperPlacementType;
}): { x: number; y: number } {
	const basePlacement = placement ? getBasePopperPlacement(placement) : null;
	const variation = placement ? getPopperPlacementVariation(placement) : null;

	const commonX = anchorRect.x + anchorRect.width / 2 - elementRect.width / 2;
	const commonY = anchorRect.y + anchorRect.height / 2 - elementRect.height / 2;

	const offsets = {
		x: anchorRect.x,
		y: anchorRect.y
	};

	switch (basePlacement) {
		case "top":
			offsets.x = commonX;
			offsets.y = anchorRect.y - elementRect.height;
			break;
		case "bottom":
			offsets.x = commonX;
			offsets.y = anchorRect.y + anchorRect.height;
			break;
		case "right":
			offsets.x = anchorRect.x + anchorRect.width;
			offsets.y = commonY;
			break;
		case "left":
			offsets.x = anchorRect.x - elementRect.width;
			offsets.y = commonY;
			break;
	}

	if (basePlacement) {
		const mainAxis = basePlacement === "top" || basePlacement === "bottom" ? "x" : "y";
		const len = mainAxis === "y" ? "height" : "width";

		switch (variation) {
			case "start":
				offsets[mainAxis] = offsets[mainAxis] - (anchorRect[len] / 2 - elementRect[len] / 2);
				break;
			case "end":
				offsets[mainAxis] = offsets[mainAxis] + (anchorRect[len] / 2 - elementRect[len] / 2);
				break;
			default:
		}
	}

	return offsets;
}
